/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { forwardRef, MouseEvent, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import MuiDialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";

import { useMediaQuery } from "../../../common/hooks/useMediaQuery";
import { useTranslate } from "../../../common/hooks/useTranslate";
import { DashboardButton } from "../DashboardButton/DashboardButton";
import { DashboardTooltip } from "../DashboardTooltip/DashboardTooltip";
import { useMergeClasses } from "../useMergeClasses";

import { zeffyDialogClasses, DEFAULT_DIALOG_MAXWIDTH, DEFAULT_DIALOG_VIBE, DialogProps } from "./types";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { IconWithBackground, IconWithBackgroundVibe } from "@/components/IconWithBackground/IconWithBackground";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Error, Success as CheckCircle, Cross as Close, Info, Warning } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

const getVibeIcon = (vibe: DialogProps["vibe"]): IconElement | undefined => {
  if (vibe === "brand") {
    return <Info />;
  }
  if (vibe === "danger") {
    return <Error />;
  }
  if (vibe === "warning") {
    return <Warning />;
  }
  if (vibe === "positive") {
    return <CheckCircle />;
  }
  return undefined;
};

const getRenderedIcon = (
  hideIcon: boolean,
  externalRenderIcon: DialogProps["renderIcon"],
  vibe: IconWithBackgroundVibe,
  icon: DialogProps["icon"],
  vibeIcon: IconElement | undefined
) => {
  if (hideIcon) {
    return undefined;
  }
  if (externalRenderIcon) {
    return externalRenderIcon();
  }
  return <IconWithBackground vibe={vibe} icon={(icon as IconElement | undefined) || vibeIcon} />;
};

/**
 * @deprecated This component is obsolete and will be removed in a future version.
 * Use `DashboardDialog` from `"@/dashboard-design-system/DashboardDialog"` instead.
 */
export const DashboardDialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      actions,
      children,
      classes: externalClasses,
      icon,
      renderIcon: externalRenderIcon,
      title,
      vibe = DEFAULT_DIALOG_VIBE,
      dialogContentId,
      fullScreenOnMobile,
      hideCloseButton,
      hideIcon = false,
      maxWidth = DEFAULT_DIALOG_MAXWIDTH,
      slots,
      sx: externalSx,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslate();
    const { isPhoneScreen } = useMediaQuery();
    const isFullScreenMobile = isPhoneScreen && fullScreenOnMobile;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const classes = useMergeClasses(zeffyDialogClasses, externalClasses);

    const vibeIcon = useMemo(() => getVibeIcon(vibe), [vibe]);

    const renderedIcon = useMemo(
      () => getRenderedIcon(hideIcon, externalRenderIcon, vibe, icon, vibeIcon),
      [hideIcon, externalRenderIcon, vibe, icon, vibeIcon]
    );

    const handleClose = () => {
      rest.onClose?.();
    };

    const handleSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
      setIsSubmitting(true);
      await actions?.main?.onClick?.(event);
      setIsSubmitting(false);
    };

    const displayCancelButton = actions?.cancel && !actions.cancel.hidden;
    const displayMainButton = actions?.main && !actions.main.hidden;

    const currentDialogClasses = useMemo(
      () => ({
        root: classes.root,
        paper: classes.paper,
        paperFullWidth: classes.paperFullWidth,
        container: classes.container,
      }),
      [classes.container, classes.paper, classes.paperFullWidth, classes.root]
    );

    const mainButton = useMemo(() => {
      if (!actions?.main || !displayMainButton) {
        return null;
      }
      const { tooltip, isLoading, ...buttonProps } = actions.main;
      const button = (
        <DashboardButton
          variant="filled"
          vibe={vibe}
          {...buttonProps}
          onClick={handleSubmit}
          isLoading={isSubmitting || isLoading}
          fullWidth
        >
          {actions?.main.children || t("dashboard", "common.confirm")}
        </DashboardButton>
      );
      if (tooltip) {
        return (
          <DashboardTooltip title={tooltip.title} placement={tooltip.placement}>
            <Grid item>{button}</Grid>
          </DashboardTooltip>
        );
      }
      return <Grid item>{button}</Grid>;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actions?.main, displayMainButton, isSubmitting, t, vibe]);

    const cancelButton = useMemo(() => {
      if (!actions?.cancel || !displayCancelButton) {
        return null;
      }
      const { tooltip, ...buttonProps } = actions.cancel;

      const button = (
        <DashboardButton variant="outlined" vibe="neutral" {...buttonProps} fullWidth>
          {actions?.cancel.children || t("dashboard", "common.cancel")}
        </DashboardButton>
      );

      if (tooltip) {
        return (
          <DashboardTooltip title={tooltip.title} placement={tooltip.placement}>
            <Grid item>{button}</Grid>
          </DashboardTooltip>
        );
      }
      return <Grid item>{button}</Grid>;
    }, [actions?.cancel, displayCancelButton, t]);

    const additionalButtons = actions?.additional?.map((buttonProps) => (
      <>
        {!buttonProps.hidden && (
          <Grid item key={buttonProps.children?.toString()}>
            <DashboardButton {...buttonProps} fullWidth />
          </Grid>
        )}
      </>
    ));

    const currentTitle = slots?.title ? (
      slots.title
    ) : title ? (
      <Box>
        <DashboardTypography
          variant="h6"
          sx={(theme) => ({
            color: theme.palette.neutralVariant[10],
            fill: theme.palette.neutralVariant[10],
          })}
        >
          {title}
        </DashboardTypography>
      </Box>
    ) : undefined;

    const hasClose = rest.onClose && !hideCloseButton;

    return (
      <MuiDialog
        {...rest}
        classes={currentDialogClasses}
        ref={ref}
        fullScreen={isFullScreenMobile || rest.fullScreen}
        maxWidth={maxWidth}
        sx={[
          (theme) => ({
            [`& .${zeffyDialogClasses.globalPadding}`]: {
              paddingLeft: theme.spacing(3),
              paddingRight: theme.spacing(3),
            },
            [`& .${zeffyDialogClasses.header}`]: {
              paddingTop: theme.spacing(3),
            },
            [`& .${zeffyDialogClasses.iconCloseContainer}`]: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            },
            [`& .${zeffyDialogClasses.iconWrapper}`]: {
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginRight: theme.spacing(1),
            },
            [`& .${zeffyDialogClasses.subtitle}`]: {
              color: theme.palette.neutralVariant[50],
            },
            [`& .${zeffyDialogClasses.defaultColor}`]: {
              color: theme.palette.neutralVariant[10],
              fill: theme.palette.neutralVariant[10],
            },
            [`& .${zeffyDialogClasses.content}`]: {
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            },
            [`& .${zeffyDialogClasses.footer}`]: {
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            },
          }),
          ...(Array.isArray(externalSx) ? externalSx : externalSx ? [externalSx] : []),
        ]}
      >
        {(currentTitle || !hideIcon || hasClose) && (
          <div className={`${classes.header} ${classes.globalPadding}`}>
            {(!hideIcon || hasClose || (currentTitle && hideIcon)) && (
              <div className={classes.iconCloseContainer}>
                <div className={classes.iconWrapper}>
                  {!hideIcon && renderedIcon}
                  {Boolean(currentTitle && hideIcon) && currentTitle}
                </div>
                {hasClose && (
                  <DashboardIconButton
                    vibe="neutral"
                    onClick={handleClose}
                    size="small"
                    aria-label={t("common", "close")}
                    data-test="close-dialog"
                  >
                    <Close />
                  </DashboardIconButton>
                )}
              </div>
            )}

            {currentTitle && !hideIcon && <Box marginTop={!hideIcon || hasClose ? 2 : 0}>{currentTitle}</Box>}
          </div>
        )}

        <MuiDialogContent className={`${classes.globalPadding} ${classes.content}`} id={dialogContentId}>
          <DashboardTypography variant="body2">{children}</DashboardTypography>
        </MuiDialogContent>
        {(displayCancelButton || displayMainButton) && (
          <Grid
            container
            className={`${classes.footer} ${classes.globalPadding}`}
            justifyContent={isPhoneScreen ? "flex-start" : "flex-end"}
            direction={isPhoneScreen ? "column" : "row"}
            spacing={1}
          >
            {isPhoneScreen ? (
              <>
                {mainButton}
                {cancelButton}
                {additionalButtons}
              </>
            ) : (
              <>
                {additionalButtons}
                {cancelButton}
                {mainButton}
              </>
            )}
          </Grid>
        )}
      </MuiDialog>
    );
  }
);

DashboardDialog.displayName = "DashboardDialog";
